import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { canGoBackInApp, resolveBackTarget } from "../hooks/smartBack";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  Building20Regular,
  Shield20Regular,
  Flash20Regular,
  LeafThree20Regular,
  People20Regular,
  Wrench20Regular,
} from "@fluentui/react-icons";
import { apiUrl } from "../utils/api";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { sectionColorHex } from "../components/risikoanalyseStep/sectionColor";
import { kfzStepFormSchema } from "../components/risikoKfzStep/schema";
import { useWindowManager } from "../components/windowManager/WindowManagerContext";
import { openContractFormWindow } from "../components/windowManager/ContractFormWindow";
import { openRiskFormWindow } from "../components/windowManager/RiskFormWindow";
import { displayContactName } from "../utils/contactDisplay";
import type { SubStepDef } from "../components/risikoanalyseStep/types";
import RiskDetailHeader from "../components/contactRisks/RiskDetailHeader";
import RiskDocumentsSection from "../components/contactRisks/RiskDocumentsSection";
import DetailHeader from "../components/shared/DetailHeader";
import QuestionBlock from "../components/risikoanalyseStep/QuestionBlock";
import { characteristicsToAnswers } from "../utils/riskCharacteristics";
import { bestaetigen } from "../components/redesign/bestaetigen";

type Characteristic = {
  nameBezeichnung?: string;
  riskCharacteristicValue?: string;
};

type FullRisk = {
  id: number;
  riskType?: string;
  name?: string;
  contactID?: number;
  contractID?: number | null;
  contract?: { id: number; versicherungsnummer?: string } | null;
  characteristics?: Characteristic[];
  versicherungssumme?: number | null;
  wertGutachten?: number | null;
  wertBemerkung?: string | null;
  /** Eltern-Risiko (z. B. die Immobilie einer PV-Anlage/eines Öltanks). */
  parentRiskObjectId?: number | null;
  parent?: RiskVerweis | null;
  /** Kind-Risiken (PV-Anlage, Öltank …), nur lesbare, nicht archivierte. */
  children?: RiskVerweis[];
};

type RiskVerweis = { id: number; name?: string | null; riskType?: string | null };

type ContactLite = {
  id?: number;
  lastname?: string;
  firstName?: string;
  displayName?: string;
  typ?: string;
  type?: { type?: string };
};

const useStyles = makeStyles({
  root: {
    paddingTop: "16px",
    paddingBottom: "32px",
    paddingLeft: "16px",
    paddingRight: "16px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    "@media (min-width: 768px)": {
      paddingTop: "24px",
      paddingBottom: "48px",
      paddingLeft: "32px",
      paddingRight: "32px",
    },
    maxWidth: "1400px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "20px",
  },
  backBtn: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("6px", "12px"),
    backgroundColor: "transparent",
    ...shorthands.border("1px", "solid", "transparent"),
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    cursor: "pointer",
    ...shorthands.borderRadius("6px"),
    ":hover": { backgroundColor: BRAND.bg },
  },
  tabsRow: {
    display: "flex",
    columnGap: "4px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    marginBottom: "8px",
  },
  tabBtn: {
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: BRAND.muted,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    fontWeight: 500,
    cursor: "pointer",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("none"),
    ...shorthands.borderBottom("2px", "solid", "transparent"),
    transitionProperty: "color, border-color",
    transitionDuration: "120ms",
    ":hover": { color: BRAND.ink },
  },
  tabBtnActive: {
    color: BRAND.ink,
    fontWeight: 700,
    borderBottomColor: BRAND.purple,
  },
  body: {
    backgroundColor: BRAND.white,
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
  },
  block: {
    marginBottom: "24px",
  },
  blockTitle: {
    margin: "0 0 12px 0",
    fontFamily: BRAND.fontAccent,
    fontSize: "1.15rem",
    color: BRAND.ink,
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  field: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  label: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  value: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    color: BRAND.ink,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    backgroundColor: BRAND.bg,
    ...shorthands.borderRadius("6px"),
    minHeight: "28px",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  /** Read-only-Darstellung strukturierter Merkmale (Listen/Objekte als JSON). */
  strukturListe: {
    listStyleType: "none",
    marginTop: 0,
    marginBottom: 0,
    paddingLeft: 0,
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
    "& > li": {
      backgroundColor: BRAND.bg,
      paddingTop: "6px",
      paddingBottom: "6px",
      paddingLeft: "10px",
      paddingRight: "10px",
      ...shorthands.borderRadius("6px"),
    },
  },
  textarea: {
    minHeight: "100px",
    resize: "vertical",
  },
  saveRow: {
    marginTop: "16px",
    display: "flex",
    justifyContent: "flex-end",
    columnGap: "10px",
  },
  primaryBtn: {
    appearance: "none",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    fontWeight: 600,
    color: BRAND.white,
    backgroundColor: BRAND.purple,
    backgroundImage: "none",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "20px",
    paddingRight: "20px",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("8px"),
    ":hover": { backgroundColor: BRAND.ink },
  },

  // ----- Sections -----
  section: {
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    overflow: "hidden",
    marginBottom: "12px",
    backgroundColor: BRAND.white,
  },
  sectionHeader: {
    width: "100%",
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.bg,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    fontWeight: 700,
    textAlign: "left",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    ...shorthands.border("none"),
    ":hover": { backgroundColor: "#f5eef6" },
  },
  sectionHeaderIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    backgroundColor: BRAND.white,
    color: BRAND.purple,
    flexShrink: 0,
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
  },
  sectionTitleText: { flexGrow: 1 },
  sectionCount: {
    color: BRAND.muted,
    fontWeight: 400,
    fontSize: "0.85rem",
  },
  sectionBody: {
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    backgroundColor: BRAND.white,
  },
  empty: {
    color: BRAND.muted,
    fontSize: "0.9rem",
    fontStyle: "italic",
  },

  // ----- Toast -----
  toast: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    backgroundColor: BRAND.ink,
    color: BRAND.white,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.borderRadius("8px"),
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    boxShadow: "0 12px 28px rgba(0,0,0,0.25)",
    zIndex: 1000,
  },
  toastSuccess: {
    backgroundColor: "#0e7c3a",
  },
  toastError: {
    backgroundColor: "#b91c1c",
  },

  // ----- Loading -----
  loading: {
    color: BRAND.muted,
    fontSize: "1rem",
    paddingTop: "60px",
    textAlign: "center",
  },
  spinnerDot: {
    display: "inline-block",
    width: "8px",
    height: "8px",
    backgroundColor: BRAND.purple,
    ...shorthands.borderRadius("999px"),
    marginRight: "4px",
    animationName: {
      "0%, 80%, 100%": { opacity: 0.2 },
      "40%": { opacity: 1 },
    },
    animationDuration: "1.4s",
    animationIterationCount: "infinite",
  },

  // ── Stacked-Card-Layout (analog ContractDetailView) ──────────────────
  sectionsStack: {
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
    marginTop: "16px",
  },
  sectionCard: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  sectionStripe: {
    height: "4px",
  },
  cardSectionHeader: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    flexWrap: "wrap",
    paddingTop: "14px",
    paddingBottom: "10px",
    paddingLeft: "20px",
    paddingRight: "20px",
  },
  cardSpacer: {
    flexGrow: 1,
  },
  cardEditBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    fontWeight: 700,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("999px"),
    transitionProperty: "border-color, color",
    transitionDuration: "120ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      color: BRAND.purple,
    },
  },
  cardSectionPill: {
    display: "inline-block",
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    ...shorthands.borderRadius("999px"),
  },
  cardSectionTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.2rem",
    fontWeight: 500,
    color: BRAND.ink,
    margin: 0,
  },
  cardSectionBody: {
    paddingTop: "8px",
    paddingBottom: "20px",
    paddingLeft: "20px",
    paddingRight: "20px",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  verweisListe: {
    listStyleType: "none",
    marginTop: 0,
    marginBottom: 0,
    paddingLeft: 0,
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
  },
  verweisLink: {
    appearance: "none",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    backgroundColor: "transparent",
    ...shorthands.border("none"),
    ...shorthands.padding("0"),
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.purple,
    cursor: "pointer",
    textAlign: "left",
    ":hover": { textDecorationLine: "underline" },
  },
  verweisTyp: {
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  elternHinweis: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    columnGap: "6px",
    ...shorthands.padding("10px", "14px"),
    backgroundColor: "#FFF8E8",
    ...shorthands.borderLeft("3px", "solid", BRAND.gold),
    ...shorthands.borderRadius("4px"),
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
  },
  cardPlaceholder: {
    paddingTop: "12px",
    paddingBottom: "12px",
    color: BRAND.muted,
    fontStyle: "italic",
    fontSize: "0.9rem",
  },
});

const formatEuro = (n: number | null | undefined): string => {
  if (n == null) return "—";
  return Number(n).toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  });
};

// Tolerates both German ("1.234,56") and plain ("1234.56") decimal input so a
// comma-typed amount isn't silently dropped to null by Number().
const parseGermanDecimal = (s: string): number | null => {
  const t = s.trim();
  if (t === "") return null;
  const normalized = t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

const buildContactDisplayName = (c: ContactLite | null): string | undefined => {
  if (!c) return undefined;
  if (c.displayName && c.displayName.trim()) return c.displayName;
  const isFirma = c.typ === "Firma" || c.type?.type === "Firma";
  if (isFirma) return c.lastname && c.lastname.trim() ? c.lastname : undefined;
  const parts = [c.lastname, c.firstName].filter((x) => x && x.trim());
  if (parts.length === 0) return undefined;
  // "Lastname, Firstname"
  return c.lastname && c.firstName ? `${c.lastname}, ${c.firstName}` : parts.join(" ");
};

type RiskKind = "kfz" | "immobilie" | "kennzahlen" | "generic";

const kindFor = (riskType: string | undefined): RiskKind => {
  const t = (riskType ?? "").toLowerCase();
  if (t.includes("kfz")) return "kfz";
  if (t.includes("immo")) return "immobilie";
  if (t.includes("kennzahl")) return "kennzahlen";
  return "generic";
};

// ----- Section grouping for Immobilie characteristics -----

type SectionDef = {
  id: string;
  title: string;
  Icon: React.ComponentType<{ style?: React.CSSProperties }>;
  match: (key: string) => boolean;
  // Optional curated label map for nicer display
  labels?: Record<string, string>;
};

/**
 * Merkmal einer Anlage aus dem Anlagen-Katalog (`sicherheit_<slug>`).
 *
 * Diese Schlüssel gehören per Konstruktion in „Sicherheit & Schadenverhütung" —
 * die Regel dort trifft sie über den Wortbestandteil `sicherheit`. Die Regeln
 * für „Energie & Heizung" und „Umweltrelevante Anlagen" sind dagegen NICHT mit
 * `^` verankert und greifen auch mitten im Slug: `sicherheit_gasfeuerloeschanlagen`
 * enthält `gas` und stand vor dieser Ausnahme in ZWEI Abschnitten. Die
 * Abschnitte filtern die Merkmalsliste unabhängig voneinander — ein „erster
 * Treffer gewinnt" gibt es nicht.
 *
 * Bewusst als Präfix-Regel und nicht als Einzelfall: der Katalog wächst (aus 5
 * Anlagen sind 28 geworden), und der nächste Slug mit `solar`, `kamin` oder
 * `tank` im Namen käme sonst genauso doppelt.
 */
const istAnlagenMerkmal = (k: string): boolean => /^sicherheit_/i.test(k);

export const IMMO_SECTIONS: SectionDef[] = [
  {
    id: "einheiten",
    title: "Einheiten & Nutzung",
    Icon: Building20Regular,
    // `eigent*` ist hier ausgezogen — es lebt jetzt in „Beteiligte & Rechte".
    // `schaeden*`/`stellplatz*` stehen hier, weil sie im Formular zum selben
    // Schritt gehören; ohne die beiden Präfixe wäre die neue Schadenhistorie
    // auf `/risks/:id` unsichtbar.
    match: (k) => /^(unit|einheit|flaeche|nutzung|gewerbe|wohn|leerstand|untervermiet|firmensitz|postanschrift|schaeden|stellplatz)/i.test(k),
    labels: {
      flaecheGesamt: "Gesamtfläche (m²)",
      flaecheSelbst: "Selbst genutzte Fläche (m²)",
      flaecheVermietet: "Vermietete Fläche (m²)",
      untervermietung: "Untervermietung",
      firmensitz: "Firmensitz",
      postanschrift: "Postanschrift",
      schaedenJa: "Schäden in den letzten Jahren",
      schaeden: "Schadenhistorie",
      schaedenDetails: "Schäden — ergänzende Bemerkungen",
      stellplatzVorhanden: "Stellplätze vorhanden",
      stellplatzArten: "Stellplatzarten",
      stellplatzDetails: "Stellplätze je Art",
      stellplatzLage: "Stellplätze — frühere Sammelangabe (Lage)",
      stellplatzAnzahl: "Stellplätze — frühere Sammelangabe (Anzahl)",
      wohnflaeche: "Wohnfläche (m²)",
      leerstand: "Leerstand",
      leerstandTeile: "Leerstand — betroffene Gebäudeteile",
      leerstandSeit: "Leerstand seit",
      leerstandBis: "Leerstand voraussichtlich bis",
      leerstandUmfang: "Leerstand — Umfang",
      leerstandAnteil: "Leerstandsanteil (%)",
      leerstandGrund: "Leerstand — Grund",
      leerstandDauer: "Leerstand — vorübergehend / längerfristig",
      leerstandWiedervermietungAb: "Wiedervermietung geplant ab",
      leerstandSanierungAb: "Sanierung geplant ab",
      leerstandPlan: "Wiedervermietung / Sanierung (frühere Angabe)",
    },
  },
  {
    id: "bedarf",
    title: "Versicherungsbedarf",
    Icon: Shield20Regular,
    match: (k) => /^versicherungsbedarf/i.test(k),
    labels: { versicherungsbedarf: "Bedarf je Sparte" },
  },
  {
    id: "grundstueck",
    title: "Grundstück, Anbauten & Sanierung",
    Icon: Building20Regular,
    // Bis 10/2026 stand keiner dieser Schlüssel in einer Regel — Außenanlagen,
    // Kunst am Bau, Anbauten und Sanierung waren auf `/risks/:id` unsichtbar.
    match: (k) => /^(aussenanlagen|kunst|anbau|sanier|eigennutzung)/i.test(k),
    labels: {
      aussenanlagen: "Außenanlagen vorhanden",
      aussenanlagenArt: "Art der Außenanlage",
      aussenanlagenWert: "Außenanlagen — Wert (€)",
      kunst: "Kunst am Bau",
      kunstBeschreibung: "Kunst am Bau — Beschreibung",
      kunstWert: "Kunst am Bau — Wert (€)",
      kunstObjekte: "Kunstobjekte",
      anbauten: "Anbauten",
      anbautenJa: "Anbauten / Nebengebäude",
      anbautenGebaeudeart: "Anbau — Gebäudeart",
      anbautenDach: "Anbau — Dach",
      anbautenEinheiten: "Anbau — Einheiten",
      anbautenBauausfuehrung: "Anbau — Bauausführung",
      saniert: "Saniert",
      sanierungUmfang: "Sanierung — Umfang",
      sanierungWas: "Sanierung — was",
      sanierungWann: "Sanierung — wann",
      sanierungen: "Sanierungen",
      sanierungGeplant: "Sanierung geplant",
      sanierungGeplantWas: "Geplante Sanierung — was",
      sanierungGeplantUmfang: "Geplante Sanierung — Umfang",
      sanierungGeplantAb: "Geplante Sanierung — ab",
      sanierungGeplantKosten: "Geplante Sanierung — Kosten (€)",
      eigennutzung: "Selbst genutzt",
    },
  },
  {
    id: "bau",
    title: "Bauausführung & Ausstattung",
    Icon: Wrench20Regular,
    // `stellung` stand bis 19.08.2026 in keiner Regel und war auf `/risks/:id`
    // damit unsichtbar — es gibt keinen Auffang-Abschnitt. Mit der neuen
    // näheren Beschreibung („Sonstiges") wäre die Lücke doppelt so groß
    // geworden; beide hängen jetzt hier, wo sie erfragt werden.
    // Achtung: `stellplatz*` bleibt trotzdem bei „Einheiten & Nutzung" — der
    // Abschnitt steht vor diesem und trägt die Regel `^stellplatz`.
    match: (k) =>
      /^(bau|dach|aussenwand|innenwand|fenster|fassade|geschoss|stockwerk|keller|fundament|treppe|stellung)/i.test(
        k,
      ),
    labels: {
      bauartklasse: "Bauartklasse",
      baujahr: "Baujahr",
      dachTyp: "Dachform",
      dachform: "Dachform",
      aussenwand: "Außenwand",
      innenwand: "Innenwand",
      geschosse: "Geschosse",
      keller: "Keller",
      kellerflaeche: "Kellerfläche (m²)",
      kellerNutzung: "Nutzung des Kellers",
      stellung: "Stellung des Gebäudes",
      stellungBeschreibung: "Stellung — nähere Beschreibung",
    },
  },
  {
    id: "sicherheit",
    title: "Sicherheit & Schadenverhütung",
    Icon: Shield20Regular,
    // `praevention` steht mit drin, seit die drei Präventionsblöcke
    // (`praeventionLeitungswasser` / `…Ueberschwemmung` / `…Sonstige`, je mit
    // `Art` und `Wert`) einen eigenen Schritt haben. Ohne die Regel wären sie
    // auf `/risks/:id` unsichtbar — einen Auffang-Abschnitt gibt es nicht.
    // KEIN `notstrom` hier: die Umwelt-Regel unten ist NICHT mit `^`
    // verankert und trägt `notstrom` bereits als Teilstring. Stünde es an
    // beiden Stellen, erschienen `notstrom`, `notstromArt` und `notstromWert`
    // doppelt — mit zwei Eingabefeldern auf denselben Schlüssel.
    match: (k) =>
      /(sicherheit|brand|alarm|sprinkler|rauchmelder|feuer|schloss|tresor|wachschutz|einbruch|videoueberw|video|praevention)/i.test(
        k,
      ),
    labels: {
      brandschutz: "Brandschutz",
      // Der Schlüssel heißt seit dem 19.08.2026 `brandschutzDokumentVorhanden`
      // („liegt ein Brandschutzkonzept vor?"). Die beiden Folgefelder der
      // früheren Fassung stehen bewusst mit drin: sie werden im Formular nicht
      // mehr erfragt, liegen aber in Bestandsdaten und bleiben hier lesbar und
      // bearbeitbar — sonst stünden sie als nackte Schlüssel in der Karte.
      brandschutzDokumentVorhanden: "Brandschutzkonzept",
      brandschutzVonWem: "Brandschutzkonzept — von wem erstellt (frühere Angabe)",
      brandschutzDatum: "Brandschutzkonzept — Datum (frühere Angabe)",
      einbruchschutz: "Einbruchmeldeanlage",
      sprinkler: "Sprinkleranlage",
      rauchmelder: "Rauchmelder",
    },
  },
  {
    id: "energie",
    title: "Energie & Heizung",
    Icon: Flash20Regular,
    match: (k) =>
      !istAnlagenMerkmal(k) &&
      /(heiz|energie|gas|pv|photovoltaik|solar|waerme|warmepumpe|kamin|stromer)/i.test(k),
    labels: {
      heizung: "Heizungsart",
      photovoltaik: "Photovoltaik",
      energietraeger: "Energieträger",
    },
  },
  {
    id: "umwelt",
    title: "Umweltrelevante Anlagen",
    Icon: LeafThree20Regular,
    match: (k) =>
      !istAnlagenMerkmal(k) &&
      /(umwelt|oel|tank|notstrom|abscheider|treibstoff|chemie|gefahrgut)/i.test(k),
    labels: {
      oeltank: "Öltank",
      notstrom: "Notstromaggregat",
      abscheider: "Öl-/Benzin-/Fettabscheider",
    },
  },
  {
    id: "beteiligte",
    title: "Beteiligte & Rechte",
    Icon: People20Regular,
    match: (k) =>
      /^(eigent|miteigent|rechnungsempfaenger|hausKonto|objektId|hv|niessbrauch|erbbau|vermietung|mieter|verpachtung|paechter|betreiber|sonstigeNutz|facility|weitereBeteiligte|gutachten|darlehen)/i.test(
        k,
      ),
    labels: {
      eigentum: "Eigentum",
      eigentuemer: "Eigentümer",
      eigentuemerName: "Eigentümer",
      eigentuemerKontaktRef: "Eigentümer (Kontakt)",
      eigentuemerArt: "Eigentümerart",
      eigentuemerArtSonstige: "Eigentümerart (Sonstige)",
      eigentuemerAnteilProzent: "Eigentumsanteil (%)",
      eigentuemerStrasse: "Eigentümer — Straße",
      eigentuemerPlz: "Eigentümer — PLZ",
      eigentuemerOrt: "Eigentümer — Ort",
      eigentuemerLand: "Eigentümer — Land",
      miteigentuemerJa: "Weitere Miteigentümer",
      miteigentuemer: "Miteigentümer",
      rechnungsempfaenger: "Rechnungsempfänger",
      rechnungsempfaengerKontaktRef: "Rechnungsempfänger (Kontakt)",
      hausKontoBank: "Hauskonto — Bank",
      hausKontoIban: "Hauskonto — IBAN",
      hausKontoBankId: "Hauskonto — Bankverbindung (Kontakt)",
      objektIdEigentuemer: "Objekt-ID des Eigentümers",
      hvBeauftragt: "Verwaltung beauftragt",
      hvArt: "Art der Verwaltung",
      hvName: "Verwaltung",
      hvKontakt: "Kontaktdaten der Verwaltung",
      hvKontaktRef: "Verwaltung (Kontakt)",
      hvObjektId: "Objekt-ID der Verwaltung",
      hvAnsprechpartner: "Ansprechpartner der Verwaltung",
      hvAnsprechpartnerId: "Ansprechpartner der Verwaltung (Kontakt)",
      niessbrauch: "Nießbrauch",
      niessbrauchWer: "Nießbrauchberechtigter",
      niessbrauchKontaktRef: "Nießbrauchberechtigter (Kontakt)",
      niessbrauchProzent: "Nießbrauch (%)",
      niessbrauchEinheit: "Nießbrauch — Einheit / Fläche",
      niessbrauchBelastetName: "Mit Nießbrauch belastet",
      niessbrauchBelastetKontaktRef: "Mit Nießbrauch belastet (Kontakt)",
      erbbaurecht: "Erbbaurecht",
      erbbauberechtigterName: "Erbbauberechtigter",
      erbbauberechtigterKontaktRef: "Erbbauberechtigter (Kontakt)",
      erbbaurechtUmfang: "Umfang des Erbbaurechts",
      erbbaugeberName: "Erbbaurechtsgeber",
      erbbaugeberKontaktRef: "Erbbaurechtsgeber (Kontakt)",
      vermietung: "Vermietet",
      mieter: "Mieter",
      verpachtung: "Verpachtet",
      paechter: "Pächter",
      betreiberAbweichend: "Abweichender Betreiber",
      betreiberName: "Betreiber",
      betreiberKontaktRef: "Betreiber (Kontakt)",
      betreiberVerantwortungsbereich: "Verantwortungsbereich des Betreibers",
      sonstigeNutzungJa: "Sonstige Nutzer vorhanden",
      sonstigeNutzer: "Sonstige Nutzer",
      facilityBeauftragt: "Facility Management beauftragt",
      facilityName: "Facility Management",
      facilityKontaktRef: "Facility Management (Kontakt)",
      facilityKontaktdaten: "Kontaktdaten Facility Management",
      weitereBeteiligteJa: "Weitere Verbundene",
      weitereBeteiligte: "Weitere verbundene Personen / Unternehmen",
      gutachten: "Gutachten",
      gutachtenErsteller: "Gutachten — Ersteller",
      gutachtenErstellerKontaktRef: "Gutachten — Ersteller (Kontakt)",
      gutachtenDatum: "Gutachten — Datum",
      gutachtenStichtag: "Gutachten — Stichtag der Bewertung",
      gutachtenGrund: "Gutachten — Grund der Bewertung",
      darlehenJa: "Darlehen",
      darlehenInstitut: "Finanzierendes Institut",
      darlehenInstitutKontaktRef: "Finanzierendes Institut (Kontakt)",
      darlehenNummer: "Darlehensnummer",
      darlehenAblauf: "Darlehen — Ablauf",
      darlehenSicherung: "Sicherungsschein / Zweckbestimmung",
    },
  },
];

const STAMMDATEN_KEYS_IMMO = new Set([
  "street",
  "postalCode",
  "city",
  "country",
  "kategorie",
  "typ",
  "bauartklasse",
  "baujahr",
  "gesamtflaeche",
  "dachTyp",
]);

// ---------------------------------------------------------------------------

const RiskDetailDispatcher = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { openWindow, windows } = useWindowManager();
  const { id } = useParams();

  const [risk, setRisk] = useState<FullRisk | null>(null);
  const [contact, setContact] = useState<ContactLite | null>(null);
  const [loading, setLoading] = useState(true);

  const [versicherungssumme, setVersicherungssumme] = useState<string>("");
  const [wertGutachten, setWertGutachten] = useState<string>("");
  const [wertBemerkung, setWertBemerkung] = useState<string>("");

  const [characteristics, setCharacteristics] = useState<Characteristic[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [toast, setToast] = useState<{ kind: "success" | "error"; message: string } | null>(null);
  const showToast = (kind: "success" | "error", message: string) => {
    setToast({ kind, message });
    setTimeout(() => setToast(null), 2400);
  };

  // ---- Load risk
  useEffect(() => {
    const run = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const res = await fetch(apiUrl(`risk-objects/${id}`));
        if (res.ok) {
          const data = (await res.json()) as FullRisk;
          setRisk(data);
          setVersicherungssumme(
            data.versicherungssumme != null ? String(data.versicherungssumme) : "",
          );
          setWertGutachten(data.wertGutachten != null ? String(data.wertGutachten) : "");
          setWertBemerkung(data.wertBemerkung ?? "");
          setCharacteristics(
            (data.characteristics ?? []).map((c) => ({
              nameBezeichnung: c.nameBezeichnung ?? "",
              riskCharacteristicValue: c.riskCharacteristicValue ?? "",
            })),
          );
        }
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id, reloadKey]);

  // ---- Load contact (for header name)
  useEffect(() => {
    const cId = risk?.contactID;
    if (!cId) return;
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(apiUrl(`contacts/${cId}`));
        if (!res.ok) return;
        const data = (await res.json()) as ContactLite;
        if (!aborted) setContact(data);
      } catch {
        // ignore
      }
    })();
    return () => {
      aborted = true;
    };
  }, [risk?.contactID]);

  const kind = useMemo(() => kindFor(risk?.riskType), [risk?.riskType]);

  // KFZ und Immobilie öffnen direkt im Schrittformular (Vollseite) — die
  // Merkmalsliste dieser Seite bleibt nur als Hintergrund und Rückfallweg
  // („Minimieren"). Einmal je Risiko: nach dem Speichern navigiert das Formular
  // auf diese Route zurück, und ein erneutes Öffnen wäre eine Endlosschleife.
  const autoGeoeffnetRef = useRef<number | null>(null);
  const [autoFormId, setAutoFormId] = useState<string | null>(null);
  useEffect(() => {
    if (!risk || (kind !== "kfz" && kind !== "immobilie")) return;
    if (autoGeoeffnetRef.current === risk.id) return;
    autoGeoeffnetRef.current = risk.id;
    setAutoFormId(
      openRiskFormWindow(openWindow, {
        editRiskId: risk.id,
        riskType: kind,
        contactId: risk.contactID ?? null,
        contactName: displayContactName(contact),
        mode: "page",
      }),
    );
    // contact ist für den Titel nur ein Zusatz — nicht darauf warten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [risk, kind, openWindow]);

  // Wird das automatisch geöffnete Formular geschlossen (Zurück, Speichern),
  // geht es dorthin zurück, wo man herkam — nicht auf diese Merkmalsliste.
  const autoFormOffen = !!autoFormId && windows.some((w) => w.id === autoFormId);
  const autoFormWarOffenRef = useRef(false);
  useEffect(() => {
    if (autoFormOffen) {
      autoFormWarOffenRef.current = true;
      return;
    }
    if (!autoFormWarOffenRef.current) return;
    autoFormWarOffenRef.current = false;
    setAutoFormId(null);
    // Wie handleBack (steht erst hinter den frühen Returns).
    const target = resolveBackTarget({
      canGoBack: canGoBackInApp(window.history.state),
      fallbackTo: risk?.contactID ? `/contacts/${risk.contactID}` : undefined,
    });
    if (target.kind === "fallback") navigate(target.to, { state: { defaultTab: "risiken" } });
    else navigate(-1);
    // Auslöser ist allein das Schließen, nicht ein neues risk-Objekt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFormOffen]);

  const charValue = (key: string): string =>
    characteristics.find((c) => c.nameBezeichnung === key)?.riskCharacteristicValue ?? "";

  const updateChar = (key: string, value: string) => {
    setCharacteristics((prev) => {
      const next = [...prev];
      const idx = next.findIndex((c) => c.nameBezeichnung === key);
      if (idx >= 0) next[idx] = { ...next[idx], riskCharacteristicValue: value };
      else next.push({ nameBezeichnung: key, riskCharacteristicValue: value });
      return next;
    });
  };

  const handleSaveAllgemein = async () => {
    if (!risk || saving) return;
    setSaving(true);
    // The backend treats `characteristics` as a full replace-set (upsert the
    // provided rows, delete the rest), so we always send the complete array
    // held in state. Top-level fields not sent (e.g. skizzeSvg) are preserved.
    const body = {
      versicherungssumme: parseGermanDecimal(versicherungssumme),
      wertGutachten: parseGermanDecimal(wertGutachten),
      wertBemerkung: wertBemerkung.trim() !== "" ? wertBemerkung : null,
      characteristics,
    };
    try {
      const res = await fetch(apiUrl(`risk-objects/${risk.id}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        showToast("success", "Risikodaten gespeichert");
        // Reload from the backend so the inputs reflect server-normalized
        // values (e.g. a German-typed "1.234,56" comes back as 1234.56) and
        // all field state stays in sync.
        setReloadKey((k) => k + 1);
      } else {
        showToast("error", "Speichern fehlgeschlagen");
      }
    } catch {
      showToast("error", "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.root}>
        <div className={styles.loading}>
          <span className={styles.spinnerDot} />
          <span className={styles.spinnerDot} />
          <span className={styles.spinnerDot} />
          <div style={{ marginTop: 8 }}>Risiko wird geladen…</div>
        </div>
      </div>
    );
  }
  if (!risk) {
    return (
      <div className={styles.root}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <ArrowLeft20Regular /> Zurück
        </button>
        <p>Risiko nicht gefunden.</p>
      </div>
    );
  }

  const handleBack = () => {
    // History-first: dorthin zurück, wo man wirklich herkam (Risiko-Tabelle,
    // Kontakt-Tab, globale Suche …). Nur beim Deep-Link (kein In-App-Vorgänger)
    // auf den Risiken-Tab des Kontakts ausweichen.
    const target = resolveBackTarget({
      canGoBack: canGoBackInApp(window.history.state),
      fallbackTo: risk.contactID ? `/contacts/${risk.contactID}` : undefined,
    });
    if (target.kind === "fallback") {
      navigate(target.to, { state: { defaultTab: "risiken" } });
    } else {
      navigate(-1);
    }
  };


  const handleDelete = async () => {
    if (!risk || deleting) return;
    if (
      !(await bestaetigen({
        titel: "Risiko löschen?",
        text: "Das Risiko wird gelöscht.",
        ja: "Löschen",
        ton: "danger",
      }))
    )
      return;
    setDeleting(true);
    try {
      const res = await fetch(apiUrl(`risk-objects/${risk.id}`), { method: "DELETE" });
      if (res.ok) {
        showToast("success", "Risiko gelöscht");
        handleBack();
      } else {
        showToast("error", "Löschen fehlgeschlagen");
      }
    } catch {
      showToast("error", "Löschen fehlgeschlagen");
    } finally {
      setDeleting(false);
    }
  };

  const contactDisplayName = buildContactDisplayName(contact);

  // -------------------- Render parts --------------------

  const renderStammdatenImmobilie = () => {
    const fields = [
      { key: "street", label: "Straße" },
      { key: "postalCode", label: "PLZ" },
      { key: "city", label: "Ort" },
      { key: "country", label: "Land" },
      { key: "kategorie", label: "Kategorie" },
      { key: "typ", label: "Typ" },
      { key: "bauartklasse", label: "Bauartklasse" },
      { key: "baujahr", label: "Baujahr" },
      { key: "gesamtflaeche", label: "Gesamtfläche (m²)" },
      { key: "dachTyp", label: "Dachform" },
    ];
    return (
      <>
        <div className={styles.grid}>
          {fields.map((f) => (
            <div key={f.key} className={styles.field}>
              <span className={styles.label}>{f.label}</span>
              <input
                className={styles.input}
                value={charValue(f.key)}
                onChange={(e) => updateChar(f.key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderStammdatenKennzahlen = () => {
    const currentYear = new Date().getFullYear();
    const yearChars = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
    return (
      <div className={styles.grid}>
        {yearChars.map((y) => {
          const key = `revenueTotal_${y}`;
          return (
            <div key={y} className={styles.field}>
              <span className={styles.label}>{`Umsatz ${y} (€)`}</span>
              <input
                className={styles.input}
                value={charValue(key)}
                onChange={(e) => updateChar(key, e.target.value)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderStammdatenGeneric = () => (
    <div className={styles.grid}>
      {characteristics.map((c, i) => (
        <div key={i} className={styles.field}>
          <span className={styles.label}>{c.nameBezeichnung || `Feld ${i + 1}`}</span>
          <input
            className={styles.input}
            value={c.riskCharacteristicValue ?? ""}
            onChange={(e) => updateChar(c.nameBezeichnung ?? "", e.target.value)}
          />
        </div>
      ))}
      {characteristics.length === 0 && (
        <p className={styles.empty}>Keine Eigenschaften erfasst.</p>
      )}
    </div>
  );

  const renderVertrag = () => {
    const fields: Array<[string, string]> = [
      ["Risikoobjekt", risk.name ?? "—"],
      ["Typ", risk.riskType ?? "—"],
      ["Versicherungssumme", formatEuro(risk.versicherungssumme)],
      ["Wert (Gutachten)", formatEuro(risk.wertGutachten)],
      ["Bemerkung", risk.wertBemerkung ?? "—"],
      ["Vertragsnummer", risk.contract?.versicherungsnummer ?? "—"],
    ];
    return (
      <div className={styles.body}>
        {kind === "immobilie" && risk.id && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <button
              className={styles.primaryBtn}
              onClick={() => {
                const riskId = risk.id!;
                openContractFormWindow(openWindow, {
                  variant: "immobilie",
                  contactId: risk.contactID ?? null,
                  contactName: displayContactName(contact),
                  riskObjectId: riskId,
                });
              }}
            >
              Neuer Vertrag (Immobilie)
            </button>
          </div>
        )}
        <div className={styles.grid}>
          {fields.map(([k, v]) => (
            <div key={k} className={styles.field}>
              <span className={styles.label}>{k}</span>
              <span className={styles.value}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // -------- Editable section bodies (inline editing) --------

  const renderWerteermittlungEdit = (): React.ReactNode => (
    <>
      <div className={styles.grid}>
        <div className={styles.field}>
          <span className={styles.label}>Versicherungssumme (€)</span>
          <input
            className={styles.input}
            value={versicherungssumme}
            inputMode="decimal"
            onChange={(e) => setVersicherungssumme(e.target.value)}
            placeholder="0,00"
          />
        </div>
        <div className={styles.field}>
          <span className={styles.label}>Wert laut Gutachten (€)</span>
          <input
            className={styles.input}
            value={wertGutachten}
            inputMode="decimal"
            onChange={(e) => setWertGutachten(e.target.value)}
            placeholder="0,00"
          />
        </div>
      </div>
      <div className={styles.field}>
        <span className={styles.label}>Bemerkung</span>
        <textarea
          className={mergeClasses(styles.input, styles.textarea)}
          value={wertBemerkung}
          onChange={(e) => setWertBemerkung(e.target.value)}
        />
      </div>
    </>
  );

  // KFZ schema sub-step rendered editable via the wizard's QuestionBlock
  // (handles conditional visibility / follow-up fields). All KFZ schema fields
  // are scalar (text/select/yesno/number/date), so a plain string answers map
  // round-trips cleanly through `characteristics`.
  const renderSchemaSubStepEdit = (subStep: SubStepDef): React.ReactNode => {
    const answers = characteristicsToAnswers(characteristics);
    return (
      <>
        {subStep.description && (
          <p style={{ color: BRAND.muted, fontSize: "0.9rem", margin: 0 }}>
            {subStep.description}
          </p>
        )}
        {subStep.questions.map((q, i) => (
          <QuestionBlock
            key={`${subStep.key}-q${i}`}
            question={q}
            answers={answers}
            onChange={(key, value) => updateChar(key, value == null ? "" : String(value))}
          />
        ))}
      </>
    );
  };

  /**
   * Strukturierte Merkmale (Listen und Objekte) liegen als JSON-Text im
   * Merkmalswert. In ein `<input>` gerendert stünde dort roher JSON — und ein
   * versehentlicher Tastendruck zerstörte die Liste. Deshalb: parsen und
   * read-only als Zusammenfassung zeigen. Gespeichert wird weiterhin der
   * unveränderte Rohwert (das Feld schreibt nichts).
   */
  const renderStrukturiert = (raw: string): React.ReactNode | null => {
    const t = raw.trim();
    if (!t.startsWith("{") && !t.startsWith("[")) return null;
    let parsed: unknown;
    try {
      parsed = JSON.parse(t);
    } catch {
      return null;
    }
    /**
     * Ein verschachtelter Wert, eine Ebene tief ausgeschrieben: aus
     * `{"garage":{"lage":"außerhalb","anzahl":"3"}}` wird
     * „garage: (lage: außerhalb, anzahl: 3)" statt „garage: …". Objekte von
     * Objekten kommen bei den Merkmalen regelmäßig vor (Stellplätze je Art,
     * Sonderausstattungen); tiefer als eine Ebene bleibt es beim „…", damit die
     * Zeile lesbar bleibt.
     */
    const wert = (v: unknown, tiefe: number): string => {
      if (v == null) return "—";
      if (typeof v !== "object") return String(v);
      if (tiefe > 0) return "…";
      const inner = Object.entries(v as Record<string, unknown>)
        .filter(([, val]) => val != null && val !== "")
        .map(([k, val]) => `${k}: ${wert(val, tiefe + 1)}`)
        .join(", ");
      return inner ? `(${inner})` : "—";
    };
    const zeile = (v: unknown): string => {
      if (v == null) return "—";
      if (typeof v !== "object") return String(v);
      const o = v as Record<string, unknown>;
      const kopf =
        (typeof o.name === "string" && o.name) ||
        (typeof o.label === "string" && o.label) ||
        "";
      const rest = Object.entries(o)
        .filter(
          ([k, val]) =>
            k !== "id" &&
            k !== "name" &&
            k !== "label" &&
            k !== "kontaktRef" &&
            val != null &&
            val !== "",
        )
        .map(([k, val]) => `${k}: ${wert(val, 0)}`);
      return [kopf, rest.join(" · ")].filter(Boolean).join(" — ") || "—";
    };
    const zeilen = Array.isArray(parsed)
      ? parsed.map(zeile)
      : [zeile(parsed)];
    return (
      <ul className={styles.strukturListe}>
        {zeilen.length === 0 ? (
          <li>Keine Einträge.</li>
        ) : (
          zeilen.map((z, i) => <li key={i}>{z}</li>)
        )}
      </ul>
    );
  };

  // Editable variant of the Immobilie topic sections: matched characteristics
  // become text inputs bound to `updateChar`.
  const renderImmoSecEdit = (sec: typeof IMMO_SECTIONS[number]): React.ReactNode => {
    const matched = characteristics.filter(
      (c) =>
        !STAMMDATEN_KEYS_IMMO.has(c.nameBezeichnung ?? "") &&
        sec.match(c.nameBezeichnung ?? ""),
    );
    if (matched.length === 0) {
      return <div className={styles.cardPlaceholder}>Keine Angaben.</div>;
    }
    return (
      <div className={styles.grid}>
        {matched.map((c) => {
          const key = c.nameBezeichnung ?? "";
          const label = sec.labels?.[key] ?? key;
          const raw = c.riskCharacteristicValue ?? "";
          const strukturiert = renderStrukturiert(raw);
          return (
            <div key={key} className={styles.field}>
              <span className={styles.label}>{label}</span>
              {strukturiert ?? (
                <input
                  className={styles.input}
                  value={raw}
                  onChange={(e) => updateChar(key, e.target.value)}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  type DetailSection = {
    key: string;
    title: string;
    pill: string;
    color: keyof typeof sectionColorHex;
    render: () => React.ReactNode;
  };


  const sectionDefs: DetailSection[] = (() => {
    const out: DetailSection[] = [];

    // 1. Werteermittlung — vor den Form-Sektionen
    out.push({
      key: "werteermittlung",
      title: "Werteermittlung",
      pill: "Werte",
      color: "orange",
      render: () => renderWerteermittlungEdit(),
    });

    // 2. Form-Sektionen je Risiko-Typ
    if (kind === "kfz") {
      const subSteps = kfzStepFormSchema.sections[0]?.subSteps ?? [];
      subSteps.forEach((s, idx) => {
        out.push({
          key: `kfz_${s.key}`,
          title: s.title,
          pill: `${idx + 1}. ${s.title}`,
          color: "purple",
          render: () => renderSchemaSubStepEdit(s),
        });
      });
    } else if (kind === "immobilie") {
      out.push({
        key: "immo_standort",
        title: "Standort & Stammdaten",
        pill: "Standort",
        color: "purple",
        render: () => renderStammdatenImmobilie(),
      });
      IMMO_SECTIONS.forEach((sec) => {
        out.push({
          key: `immo_${sec.id}`,
          title: sec.title,
          pill: sec.title,
          color: "purple",
          render: () => renderImmoSecEdit(sec),
        });
      });
    } else if (kind === "kennzahlen" || kind === "generic") {
      out.push({
        key: "stammdaten",
        title: "Stammdaten",
        pill: "Daten",
        color: "purple",
        render: () =>
          kind === "kennzahlen" ? renderStammdatenKennzahlen() : renderStammdatenGeneric(),
      });
    }

    // 3. Dokumente & Fotos — vor den Verträgen.
    // Ohne diese Sektion wären Uploads aus der Erfassungsmaske nirgends
    // sichtbar und ein fehlgeschlagener Upload nicht nachholbar.
    if (risk.id) {
      out.push({
        key: "dokumente",
        title: "Dokumente & Fotos",
        pill: "Dateien",
        color: "purple",
        render: () => (
          <RiskDocumentsSection riskObjectId={risk.id!} contactId={risk.contactID ?? null} />
        ),
      });
    }

    // 3b. Zugehörige Risikoobjekte (PV-Anlage, Öltank …) — Punkt J der
    // Fachabstimmung 31.08.2026: eigene Risiken, die am Gebäude hängen.
    if ((risk.children ?? []).length > 0) {
      out.push({
        key: "kinder",
        title: "Zugehörige Risikoobjekte",
        pill: "Objekte",
        color: "purple",
        render: () => (
          <ul className={styles.verweisListe}>
            {(risk.children ?? []).map((k) => (
              <li key={k.id}>
                <button
                  type="button"
                  className={styles.verweisLink}
                  onClick={() => navigate(`/risks/${k.id}`)}
                >
                  {k.riskType && <span className={styles.verweisTyp}>{k.riskType}</span>}
                  <span>{k.name?.trim() || `Risiko #${k.id}`}</span>
                </button>
              </li>
            ))}
          </ul>
        ),
      });
    }

    // 4. Verträge — am Ende
    out.push({
      key: "vertraege",
      title: "Verträge",
      pill: "Verträge",
      color: "pillarOrange",
      render: () => renderVertrag(),
    });

    return out;
  })();

  return (
    <div className={styles.root}>
      <DetailHeader
        title={risk.name && risk.name.trim() ? risk.name : (risk.riskType ?? "Risiko")}
        subtitle={contactDisplayName}
        badges={risk.riskType ? [{ label: "Typ", value: risk.riskType }] : []}
        onBack={handleBack}
        onSave={handleSaveAllgemein}
        onDelete={handleDelete}
        isSaving={saving}
        isDeleting={deleting}
        actionButtons={
          risk.id && kind
            ? [
                {
                  label: "Im Formular bearbeiten",
                  appearance: "subtle",
                  onClick: () =>
                    openRiskFormWindow(openWindow, {
                      editRiskId: risk.id,
                      riskType:
                        kind === "kfz" ? "kfz" : kind === "immobilie" ? "immobilie" : undefined,
                      contactId: risk.contactID ?? null,
                      contactName: displayContactName(contact),
                    }),
                },
              ]
            : []
        }
      />

      <RiskDetailHeader risk={{ ...risk, contactDisplayName }} />

      {risk.parent && (
        <div className={styles.elternHinweis}>
          <span>Gehört zu</span>
          <button
            type="button"
            className={styles.verweisLink}
            onClick={() => navigate(`/risks/${risk.parent!.id}`)}
          >
            {risk.parent.name?.trim() || `${risk.parent.riskType ?? "Risiko"} #${risk.parent.id}`}
          </button>
        </div>
      )}

      <div className={styles.sectionsStack}>
        {sectionDefs.map((s) => {
          const color = sectionColorHex[s.color];
          return (
            <section key={s.key} className={styles.sectionCard}>
              <div className={styles.sectionStripe} style={{ backgroundColor: color }} />
              <div className={styles.cardSectionHeader}>
                <span className={styles.cardSectionPill} style={{ backgroundColor: color }}>
                  {s.pill}
                </span>
                <h2 className={styles.cardSectionTitle}>{s.title}</h2>
                <span className={styles.cardSpacer} />
              </div>
              <div className={styles.cardSectionBody}>{s.render()}</div>
            </section>
          );
        })}
      </div>

      {toast && (
        <div
          className={mergeClasses(
            styles.toast,
            toast.kind === "success" ? styles.toastSuccess : styles.toastError,
          )}
          role="status"
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default RiskDetailDispatcher;
